import classNames from 'clsx'
import { bool, number, object, objectOf, oneOfType, string } from 'prop-types'

import './ImageWithDiscountBadge.scss'
import { getPlaceholderImage, isCdnUrlHook } from 'shared/utils/imageUtils'
import { isEmpty as isObjectEmpty } from 'shared/utils/objectUtils'
import CdnImage from 'views/components/atoms/CdnImage'
import DiscountBadge from 'views/components/atoms/DiscountBadge'
import Image from 'views/components/atoms/Image'
import { sizesPropType } from 'views/utils/propTypes'

const ImageWithDiscountBadge = ({
  imageUrl,
  sizes,
  srcWidth,
  srcHeight,
  minWidth,
  maxWidth,
  alt,
  discountPercent,
  thumbnail,
  lazyLoad,
  tighterThumbnailWithDarkBackground,
  prescriptionMedicine,
  isERX,
  imageAttrs,
}) => {
  const badgeLabel = discountPercent ? (
    <span className="a-CircleBadge__inner__content">
      &minus;
      <strong className="a-CircleBadge__inner__content-strong">
        {discountPercent}
      </strong>
      %
    </span>
  ) : null

  const imageClassNameFinal = classNames(
    'a-fullwidth-image m-ImageWithDiscountBadge__image',
    {
      'a-thumbnail-image': thumbnail,
    }
  )

  // For several reasons we need to check the imageUrl deeply
  let sanitizedImgUrl = ''
  if (!imageUrl || isObjectEmpty(imageUrl)) {
    sanitizedImgUrl = getPlaceholderImage(prescriptionMedicine)
  } else if (typeof imageUrl === 'object') {
    // Sometimes (PIM Product Call) the imageUrl is an object
    sanitizedImgUrl = imageUrl.src
  } else {
    sanitizedImgUrl = imageUrl
  }

  const imageWrapperClassNameString = classNames('m-ImageWithDiscountBadge', {
    'm-ImageWithDiscountBadge--tighterThumbnailWithDarkBackground':
      thumbnail && tighterThumbnailWithDarkBackground,
    'm-ImageWithDiscountBadge--eRX': isERX,
  })

  const isEligibleForCdnImage = Boolean(
    sizes?.base && minWidth && maxWidth && isCdnUrlHook(sanitizedImgUrl)
  )

  return (
    <div className={imageWrapperClassNameString}>
      <div className="responsive-image-overlay">
        {isEligibleForCdnImage ? (
          <CdnImage
            fileName={sanitizedImgUrl}
            sizes={sizes}
            alt={alt}
            imgClassName={imageClassNameFinal}
            minWidth={minWidth}
            maxWidth={maxWidth}
            maxHeight={maxWidth}
            lazyLoad={lazyLoad}
            {...imageAttrs}
          />
        ) : (
          <Image
            src={sanitizedImgUrl}
            alt={alt}
            className={imageClassNameFinal}
            width={srcWidth}
            height={srcHeight}
            lazyLoad={lazyLoad}
            imageAttrs={imageAttrs}
          />
        )}
      </div>

      {badgeLabel && (
        <div className="m-ImageWithDiscountBadge__badge">
          <DiscountBadge discount={discountPercent}>{badgeLabel}</DiscountBadge>
        </div>
      )}
    </div>
  )
}

ImageWithDiscountBadge.propTypes = {
  alt: string.isRequired,
  discountPercent: number,
  imageUrl: oneOfType([string, object]).isRequired,
  isERX: bool,
  sizes: sizesPropType,
  srcWidth: number,
  srcHeight: number,
  minWidth: number,
  maxWidth: number,
  lazyLoad: bool,
  prescriptionMedicine: bool,
  thumbnail: bool,
  tighterThumbnailWithDarkBackground: bool,
  imageAttrs: objectOf(oneOfType([string, number])),
}

ImageWithDiscountBadge.defaultProps = {
  discountPercent: null,
  isERX: false,
  sizes: null,
  srcWidth: null,
  srcHeight: null,
  minWidth: null,
  maxWidth: null,
  lazyLoad: false,
  prescriptionMedicine: false,
  thumbnail: false,
  tighterThumbnailWithDarkBackground: false,
  imageAttrs: null,
}

export default ImageWithDiscountBadge
